/* eslint-disable no-restricted-globals -- File / FileReader are browser APIs */
import {
    storeAccountApiErrorBodySchema,
    StoreAccountApiErrorBodyType,
    StoreAccountLinksRowType,
} from '../../../../../../../../service/source-settings/store-accounts/store-accounts-hook';
import {deserializeApiError} from '../../../../../../../../util/api-adapter';
import {ApiError, HttpError} from '../../../../../../../../util/error';

import {
    AppstoreAppsBrandItemEnum,
    AppstoreAppsFormEnum,
    AppstoreAppsFormType,
} from './appstore-apps-form/appstore-apps-form-type';

export function readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.addEventListener('load', (): void => {
            const result = reader.result;

            if (typeof result !== 'string' || !result.includes(',')) {
                reject(new Error('Invalid file read result'));
                return;
            }

            const base64 = result.split(',')[1];

            if (!base64) {
                reject(new Error('Empty base64 payload'));
                return;
            }

            resolve(base64);
        });

        reader.addEventListener('error', (): void => {
            reject(reader.error ?? new Error('FileReader error'));
        });

        reader.readAsDataURL(file);
    });
}

type StoreAccountErrorMessageMapType = Partial<Record<string, string>>;

// eslint-disable-next-line complexity
export function getStoreAccountErrorMessage(
    error: unknown,
    fallbackText: string,
    codeMessageMap: StoreAccountErrorMessageMapType = {}
): string {
    const parsed = deserializeApiError<StoreAccountApiErrorBodyType>(
        'store-accounts',
        storeAccountApiErrorBodySchema,
        error
    );

    if (parsed?.code) {
        return codeMessageMap[parsed.code] ?? parsed.status ?? fallbackText;
    }

    if (parsed?.status) {
        return parsed.status;
    }

    if (error instanceof HttpError) {
        return fallbackText;
    }

    if (error instanceof SyntaxError) {
        return fallbackText;
    }

    if (error instanceof TypeError) {
        return fallbackText;
    }

    if (error instanceof ApiError || error instanceof Error) {
        return error.message || fallbackText;
    }

    return fallbackText;
}

export function buildStoreAccountBindLinks(
    values: AppstoreAppsFormType
): Array<{brandId: number; apps: Array<string>}> {
    return values.brandApps.flatMap((row) =>
        typeof row.brandId === 'number'
            ? [
                  {
                      brandId: row.brandId,
                      apps: row.apps,
                  },
              ]
            : []
    );
}

/** All appId values selected across brandApps rows. */
export function collectSelectedAppIdsFromForm(values: AppstoreAppsFormType): Set<string> {
    return new Set(values.brandApps.flatMap((row) => row.apps));
}

/** Apps that were linked before save but are no longer selected. */
export type StoreAccountDetachedLinkAppType = {
    appId: string;
    appName: string;
};

export function mapStoreAccountLinksRowsToFormValues(links: Array<StoreAccountLinksRowType>): AppstoreAppsFormType {
    const brandApps = links
        .filter((row) => Number.isFinite(row.brandId))
        .map((row) => ({
            [AppstoreAppsBrandItemEnum.brandId]: row.brandId,
            [AppstoreAppsBrandItemEnum.apps]: row.apps,
        }));

    return {
        [AppstoreAppsFormEnum.brandApps]: brandApps.length > 0 ? brandApps : [{[AppstoreAppsBrandItemEnum.apps]: []}],
    };
}

export function getDetachedLinkedAppsFromLinks(
    previousLinks: Array<StoreAccountLinksRowType>,
    selectedAppIds: Set<string>
): Array<StoreAccountDetachedLinkAppType> {
    return previousLinks.flatMap((row) => row.linkApps).filter((app) => !selectedAppIds.has(app.appId));
}
